import {
  FreightRail,
  IDestinationExtension,
  IQuoteExtension,
  IQuotesByDestination,
  OriginPrice,
} from "../../../types/IQuotes";
import ILocation, { IRailStation } from "../../../types/ILocation";
import IModes from "../../../types/IModes";
import { Quote } from "../../../types/IMultiLocationQuoteMobile";
import NitroService from "../../../utils/nitro.service";
import { toModes } from "../../../utils/utils";
import RouteValidationService from "../../competitiveAnalysisModal/components/RouteValidation/lib/service";
import IRouteValidationResult from "../../competitiveAnalysisModal/components/RouteValidation/types/IRouteValidation";
import { findTypeMatchedOccupant } from "../../mobile/multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import { PricePreference } from "./KochDlvdOfferToggle";

const backgroundGreen = "#DCFCE7";
const backgroundYellow = "#FEF3C7E5";
const backgroundRed = "#FEE2E2";
const textGreen = "#166534";
const textYellow = "#92400E";
const textRed = "#991B1B";
const deltaGreen = "#059669";
const deltaRed = "#B91C1C";
const textGrey = "#152b36";
export type MissingTruckFreightDesktopSections = {
  missingMilesOrigins: {
    quote: IQuoteExtension;
    mileageRequested: boolean;
  }[];
  noRouteOrigins: IQuoteExtension[];
  expiredOrigins: IQuoteExtension[];
  outsideMileageBandOrigins: {
    quote: IQuoteExtension;
    mileage: number;
  }[];
};
export type MissingRailFreightDesktopSections = {
  missingProductOrigins?: IQuoteExtension[];
  originsNotRequested: IQuoteExtension[];
  originsWithRateNotFound: IQuoteExtension[];
  originsInProgress: IQuoteExtension[];
  originsWithPublicRate: IQuoteExtension[];
};

/**
 * Only needed when Origin Manager is false
 */
export const hasProductForQuote = (
  quote: IQuoteExtension | IQuoteExtension,
  selectedProduct: string,
): boolean => {
  const occupants = Array.isArray(quote.origin.details?.occupants)
    ? quote.origin.details?.occupants ?? []
    : [];
  const normalizedProduct = selectedProduct.trim().toUpperCase();

  return occupants.some(
    (occupant: { products?: { product?: string }[] }) =>
      occupant.products?.some(
        (entry) => entry.product?.trim().toUpperCase() === normalizedProduct,
      ) ?? false,
  );
};
export interface FobDifferenceResult {
  backgroundColor?: string;
  netbackTextColor: string;
  deltaColor?: string;
  netbackDelta: string;
  hasBackgroundChange: boolean;
}

// TODO: old code remove
export const calculateStations = (
  multiRailStation: boolean,
  location?: ILocation,
): IRailStation[] => {
  if (!location) return [];
  const stations = multiRailStation
    ? location.railStations
    : [
        {
          stationState: location.stationState || "",
          scrsId: location.scrsId || "",
          carriers: location.carriers,
        },
      ];
  return stations;
};

// narrows calculateStations' result down to the single station a quoted rate was actually run for
export const findStationForRate = (
  multiRailStation: boolean,
  location: ILocation | undefined,
  stationLabel: string,
): IRailStation => {
  const stations = calculateStations(multiRailStation, location);
  if (!multiRailStation) return stations[0];

  const matched = stations.find(
    (station) =>
      station.stationState.trim().toLowerCase() ===
      stationLabel.trim().toLowerCase(),
  );
  return matched || stations[0];
};

/**
 * Delta calculation for FOB Netback vs. Price Range
 * If FOB Netback is above the upper bound:
delta = FOB Netback − upper bound (positive, shown like $+10).

If FOB Netback is below the lower bound:

delta = lower bound − FOB Netback (negative, shown like $-2).

If final-netback-price is inside the range:
delta is empty (no numeric delta shown).

Example with price range (128 - 110):

FOB Netback 138 → $+10
FOB Netback 108 → $-2

 */

export type FobRangeBounds = {
  upperBound: number;
  lowerBound: number;
};

export const parseFobSubTextRange = (
  fobSubText: string,
): FobRangeBounds | null => {
  const matches = fobSubText.match(/-?\d+(?:\.\d+)?/g);
  if (!matches || matches.length === 0) {
    return null;
  }

  const numericValues = matches
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (numericValues.length === 0) {
    return null;
  }

  if (numericValues.length === 1) {
    return {
      upperBound: numericValues[0],
      lowerBound: numericValues[0],
    };
  }

  const first = numericValues[0];
  const second = numericValues[1];
  return {
    upperBound: Math.max(first, second),
    lowerBound: Math.min(first, second),
  };
};

export const calculateFobDifference = (
  finalNetbackValue: number,
  initialNetback: number,
  alternative: number,
  isNetbackModified: boolean,
  rangeBounds?: FobRangeBounds | null,
): FobDifferenceResult => {
  if (!isNetbackModified) {
    return {
      backgroundColor: undefined,
      netbackTextColor: textGrey,
      deltaColor: undefined,
      netbackDelta: "",
      hasBackgroundChange: false,
    };
  }

  let hasBackgroundChange = false;
  let backgroundColor;
  let netbackDelta = "";
  let netbackTextColor = "";
  let deltaColor;

  // TODO: remove alternative stuff after origin manager deployed
  const upperBound = rangeBounds?.upperBound ?? initialNetback;
  const lowerBound = rangeBounds?.lowerBound ?? alternative;

  if (finalNetbackValue > upperBound) {
    const difference = Number((finalNetbackValue - upperBound).toFixed(0));
    backgroundColor = backgroundGreen;
    netbackTextColor = textGreen;
    deltaColor = deltaGreen;
    hasBackgroundChange = true;
    netbackDelta = `$+${difference}`;
  } else if (finalNetbackValue < lowerBound) {
    const difference = (lowerBound - finalNetbackValue).toFixed(0);
    backgroundColor = backgroundRed;
    netbackTextColor = textRed;
    deltaColor = deltaRed;
    netbackDelta = `$-${difference}`;
    hasBackgroundChange = true;
  } else if (finalNetbackValue < upperBound && finalNetbackValue > lowerBound) {
    backgroundColor = backgroundYellow;
    netbackTextColor = textYellow;
    hasBackgroundChange = true;
  } else {
    backgroundColor = "transparent";
    netbackTextColor = textGrey;
  }

  return {
    backgroundColor,
    netbackTextColor,
    deltaColor,
    netbackDelta,
    hasBackgroundChange,
  };
};

// sort destinations so that city pins are at the bottom of the list
export const sortDestinations = (
  destinations: IQuotesByDestination[],
  queryType: string,
) => {
  // if sorting by location, sort alphabetically by location name and then place city pins at the bottom since they are less specific than terminal pins
  if (queryType === "location") {
    const alphabetical = [...destinations].sort((a, b) =>
      a.destination.name.localeCompare(b.destination.name),
    );
    return [...alphabetical].sort((a, b) => {
      if (a.destination.isCity && !b.destination.isCity) return 1;
      if (!a.destination.isCity && b.destination.isCity) return -1;
      return 0;
    });
  }
  // if sorting by party, sort alphabetically by state then city since there can be multiple destinations for a single party
  if (queryType === "party") {
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    return [...destinations].sort((a, b) => {
      const byState = collator.compare(
        a.destination.stateOrProvince || "",
        b.destination.stateOrProvince || "",
      );
      if (byState !== 0) return byState;
      return collator.compare(
        a.destination.city || "",
        b.destination.city || "",
      );
    });
  }
  return destinations;
};

// does not sort freight-less quotes to the bottom since they are already sorted that way by default from the api
export const sortQuotesByDlvdOffer = (
  quotes: IQuoteExtension[],
): IQuoteExtension[] => {
  return [...quotes].sort((a, b) => {
    const dlvdOfferA = a.initialOffer != null ? Number(a.initialOffer) : null;
    const dlvdOfferB = b.initialOffer != null ? Number(b.initialOffer) : null;

    if (dlvdOfferA === null && dlvdOfferB === null) return 0;
    // place nulls at the end
    if (dlvdOfferA === null) return 1;
    if (dlvdOfferB === null) return -1;

    // ascending order
    return dlvdOfferA - dlvdOfferB;
  });
};

// TODO: remove the old untyped versions of these functions in utils.ts
const priceToNumber = (value: OriginPrice): number | null =>
  typeof value === "number" ? value : null;

// netback for a single IQuoteExtension quote based on its mode/competitor status and the koch dlvd toggle
export const resolveNetback = (
  quote: IQuoteExtension,
  kochDlvdMode: PricePreference,
): number | null => {
  const mode = quote.mode.toUpperCase();

  if (quote.competitor) {
    if (mode === "TRUCK") return priceToNumber(quote.truckPrice);
    if (mode === "RAIL") return priceToNumber(quote.railPrice);
    return null;
  }

  if (mode === "TRUCK") {
    return priceToNumber(
      kochDlvdMode.truck === "px" ? quote.postedPrice : quote.truckPrice,
    );
  }
  if (mode === "RAIL") {
    return priceToNumber(
      kochDlvdMode.rail === "px" ? quote.postedPrice : quote.railPrice,
    );
  }
  return null;
};

// ranks quotes with a resolvable netback first, then ascending by delivered price (netback + freight)
export const sortQuotesByNetbackAndDlvd = (
  quotes: IQuoteExtension[],
  kochDlvdMode: PricePreference,
): IQuoteExtension[] =>
  [...quotes].sort((a, b) => {
    const netbackA = resolveNetback(a, kochDlvdMode);
    const netbackB = resolveNetback(b, kochDlvdMode);
    const hasNetbackA = netbackA !== null;
    const hasNetbackB = netbackB !== null;

    if (hasNetbackA && !hasNetbackB) return -1;
    if (!hasNetbackA && hasNetbackB) return 1;
    if (!hasNetbackA || !hasNetbackB) return 0;

    const dlvdA = netbackA + (a.freight?.totalFreight ?? 0);
    const dlvdB = netbackB + (b.freight?.totalFreight ?? 0);
    return dlvdA - dlvdB;
  });

// toggles the mode and updates the destination's inbound modes and other details accordingly
export const updateInboundModes = async (
  destination: IDestinationExtension,
  value: boolean,
  modeToToggle?: string,
) => {
  if (!destination.details || !modeToToggle) return;
  const newModes = {
    ...(toModes(
      destination.details.inboundModes || [],
      destination.details.outboundModes || [],
    ) || {}),
  };
  (newModes as any)[modeToToggle.toUpperCase()].inbound = value;

  const params = {
    id: destination.id,
    modes: convertModeParams(newModes),
    equipmentTypes: destination.details.equipmentTypes || [],
    threeLegsAllowed: destination.details.threeLegsAllowed || false,
    railStations: calculateStations(
      !!destination.details.railStations?.length,
      destination.details,
    ).map(({ scrsId, stationState, carriers }) => ({
      scrsId,
      stationState,
      carriers: carriers.map((carrier) => carrier.id),
    })),
  };
  await NitroService.updateLocationModes2(params);
};

export function useAnchorPosition(anchor: HTMLElement | null) {
  if (!anchor) {
    return { top: 0, left: 0 };
  }
  const rectangle = anchor.getBoundingClientRect();

  // Convert viewport coordinates (rect) to document coordinates using scroll offsets.
  const top = rectangle.bottom + window.scrollY;
  const left = rectangle.left + window.scrollX;
  return { top, left };
}

export const normalizeDestinationName = (input: string): string => {
  // Split while keeping separators (spaces or dashes) in the result
  return input
    .split(/([ -]+)/) // capture separators so they remain
    .map((part) => {
      // If it's a separator chunk (spaces/dashes), keep as-is
      if (/^[ -]+$/.test(part)) return part;

      // Sentence case a "word": first char upper, rest lower
      // (Handles 1-character words safely)
      return part.length > 0
        ? part[0].toUpperCase() + part.slice(1).toLowerCase()
        : part;
    })
    .join("");
};

export const convertModeParams = (
  modes?: IModes,
): { id: string; inbound: boolean; outbound: boolean }[] => {
  if (!modes) return [];
  return Object.values(modes).map((mode) => ({
    id: mode.name,
    inbound: mode.inbound,
    outbound: mode.outbound,
  }));
};

export const toMobileQuote = (quote: IQuoteExtension): Quote => ({
  mode: quote.mode,
  competitor: quote.competitor ?? false,
  minPrice: null,
  maxPrice: null,
  origin: {
    id: quote.origin.id,
    name: quote.origin.name,
    pinType: quote.origin.pinType,
  },
  freight: quote.freight
    ? {
        rate: (quote.freight as { rate?: number }).rate ?? 0,
        totalFreight: (quote.freight as { totalFreight?: number }).totalFreight,
      }
    : undefined,
});

export const buildMissingTruckOriginSections = async (
  quotes: IQuoteExtension[],
  destination: IDestinationExtension,
  filters: { product: string; month: string },
): Promise<MissingTruckFreightDesktopSections> => {
  const missingMilesOrigins: {
    quote: IQuoteExtension;
    mileageRequested: boolean;
  }[] = [];
  const noRouteOrigins: IQuoteExtension[] = [];
  const expiredOrigins: IQuoteExtension[] = [];
  const outsideMileageBandOrigins: {
    quote: IQuoteExtension;
    mileage: number;
  }[] = [];
  const originalIndex = new Map(quotes.map((q, i) => [q.origin.id, i]));
  await Promise.all(
    quotes.map(async (quote) => {
      try {
        const occupants =
          quote.origin.details && Array.isArray(quote.origin.details.occupants)
            ? quote.origin.details.occupants
            : [];

        const matchedOccupant = findTypeMatchedOccupant(
          occupants,
          toMobileQuote(quote),
        );
        const validation = await RouteValidationService.validate({
          mode: "TRUCK",
          product: filters.product,
          originLocationId: quote.origin.id,
          originOccupantId: matchedOccupant?.id || "",
          destinationId: destination.id,
          shipDate: filters.month || "",
          isCity: !!destination.isCity,
        });
        if (validation.originCanStoreProduct === false) {
          return;
        }
        if (!validation.hasTruckRoute) {
          noRouteOrigins.push(quote);
          return;
        }
        if (!validation.hasTruckMileage) {
          missingMilesOrigins.push({
            quote,
            mileageRequested: validation.mileageRequested ?? false,
          });
          return;
        }
        if (!validation.hasValidTruckRateForShipWindow) {
          expiredOrigins.push(quote);
          return;
        }
        if (!validation.hasValidTruckRateForShipWindowAndMileage) {
          outsideMileageBandOrigins.push({
            quote,
            mileage: validation.truckMileage,
          });
        }
      } catch (error) {
        console.log("ERROR", { error });
      }
    }),
  );
  const byIndex = (a: IQuoteExtension, b: IQuoteExtension) =>
    (originalIndex.get(a.origin.id) ?? 0) -
    (originalIndex.get(b.origin.id) ?? 0);

  missingMilesOrigins.sort((a, b) => byIndex(a.quote, b.quote));
  noRouteOrigins.sort(byIndex);
  expiredOrigins.sort(byIndex);
  outsideMileageBandOrigins.sort(
    (a, b) =>
      (originalIndex.get(a.quote.origin.id) ?? 0) -
      (originalIndex.get(b.quote.origin.id) ?? 0),
  );

  return {
    missingMilesOrigins,
    noRouteOrigins,
    expiredOrigins,
    outsideMileageBandOrigins,
  };
};

const handleCompetitorQuotes = (
  quote: IQuoteExtension,
  validation: IRouteValidationResult,
): "not-requested" | "rate-not-found" | "in-progress" => {
  if (!validation.railRateRequested) {
    return "not-requested";
  }
  if (validation.railRateRequestFailed) {
    return "rate-not-found";
  }
  return "in-progress";
};

const handleKochQuotes = (
  quote: IQuoteExtension,
  validation: IRouteValidationResult,
): "not-requested" | "rate-not-found" | "in-progress" | "public-rate" => {
  const freight = quote.freight as FreightRail | undefined;
  if (!freight) {
    if (!validation.railRateRequested) {
      return "not-requested";
    }
    if (validation.railRateRequestFailed) {
      return "rate-not-found";
    }
    return "in-progress";
  }
  return "public-rate";
};

export const buildMissingRailOriginSections = async (
  quotes: IQuoteExtension[],
  destination: IDestinationExtension,
  filters: { product: string; month: string },
): Promise<MissingRailFreightDesktopSections> => {
  const originsNotRequested: IQuoteExtension[] = [];
  const originsWithRateNotFound: IQuoteExtension[] = [];
  const originsInProgress: IQuoteExtension[] = [];
  const originsWithPublicRate: IQuoteExtension[] = [];
  const originalIndex = new Map(quotes.map((q, i) => [q.origin.id, i]));
  await Promise.all(
    quotes.map(async (quote) => {
      try {
        const occupants =
          quote.origin.details && Array.isArray(quote.origin.details.occupants)
            ? quote.origin.details.occupants
            : [];
        const matchedOccupant = findTypeMatchedOccupant(
          occupants,
          toMobileQuote(quote),
        );
        const validation = await RouteValidationService.validate({
          mode: "RAIL",
          product: filters.product,
          originLocationId: quote.origin.id,
          originOccupantId: matchedOccupant?.id || "",
          destinationId: destination.id,
          shipDate: filters.month || "",
          isCity: !!destination.isCity,
        });
        // - origin cannot store product
        if (quote.competitor) {
          const competitorStatus = handleCompetitorQuotes(quote, validation);
          if (competitorStatus === "not-requested") {
            originsNotRequested.push(quote);
          } else if (competitorStatus === "rate-not-found") {
            originsWithRateNotFound.push(quote);
          } else {
            originsInProgress.push(quote);
          }
        } else {
          const kochStatus = handleKochQuotes(quote, validation);
          if (kochStatus === "not-requested") {
            originsNotRequested.push(quote);
          } else if (kochStatus === "rate-not-found") {
            originsWithRateNotFound.push(quote);
          } else if (kochStatus === "in-progress") {
            originsInProgress.push(quote);
          } else {
            originsWithPublicRate.push(quote);
          }
        }
        // - origin has no rail freight rate on file and user has not requested a rail rate yet
      } catch (error) {
        console.log("ERROR", { error });
      }
    }),
  );
  const byIndex = (a: IQuoteExtension, b: IQuoteExtension) =>
    (originalIndex.get(a.origin.id) ?? 0) -
    (originalIndex.get(b.origin.id) ?? 0);

  originsNotRequested.sort(byIndex);
  originsWithRateNotFound.sort(byIndex);
  originsInProgress.sort(byIndex);
  originsWithPublicRate.sort(byIndex);

  return {
    originsNotRequested,
    originsWithRateNotFound,
    originsInProgress,
    originsWithPublicRate,
  };
};

// generates a unique key for each quote row based on origin id, mode, minCars (if rail), and competitor status
export const getQuoteRowKey = (
  quote?: IQuoteExtension | IQuoteExtension,
): string => {
  if (!quote) {
    return "";
  }
  const minCars =
    quote.freight && "minCars" in quote.freight
      ? quote.freight.minCars
      : "no-mincars";

  return `${quote.origin.id}-${quote.mode}-${minCars}-${
    quote.competitor ? "competitor" : "koch"
  }`;
};

// generates a unique key for each missing origin row based on origin id, destination id, and competitor status
export const getMissingOriginRowKey = (
  destinationId: string,
  quote: IQuoteExtension,
): string => {
  return `missing-product-${quote.competitor ? "comp" : "koch"}-${
    quote.origin.id
  }-${destinationId}`;
};

// this function checks if a rail quotes freight rate is quoted or the rail team has been notified.
export const isRailFreightQuotedOrNotified = (
  notifiedRailRateRows: Set<string>,
  quote?: IQuoteExtension,
): { isQuoted: boolean; isNotified: boolean } => {
  // if the quote is not a rail quote or does not have freight, return false for both isQuoted and isNotified
  if (
    !quote ||
    !quote.freight ||
    quote.mode.toUpperCase() !== "RAIL" ||
    quote.competitor
  ) {
    return { isQuoted: false, isNotified: false };
  }
  const kochRowKey = getQuoteRowKey(quote);
  const freight = quote.freight as FreightRail;

  const isQuoted = freight.rateType === "Quote";

  // check if the rail team has been notified for the quote
  // first check is for local state (did the user click the button?), second check is for global state (does the backend know the rail team has been notified for this quote?)
  // checking if quoted because once the rail team is notified and saves the rate then the type is no longer quoted
  const isNotified =
    (kochRowKey && notifiedRailRateRows.has(kochRowKey)) ||
    (isQuoted && freight.railTeamNotified);

  return { isQuoted, isNotified };
};
